import { ReactNode } from "react";
import { Button, Grid } from "@mui/material";
import Filter from "common/Select/SelectMultiFilter";
import { useTranslation } from "react-i18next";
import DynamicSelect from "common/Select/Select";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
import { useSelector } from "react-redux";
import StyledField from "common/TextField/TexField1";
import dayjs from "dayjs";
import DateRangePicker from "common/Pickers/DateRangePicker";

interface ExpensesSearchHeaderComponentProps {
  handleChangeFilter: (event: any) => void;
  handleChangeSearch: (field: string, value: any) => void;
  handleSubmitSearch: () => void;
  searchFormData: any;
  filter: string[];
  clearSearch: () => void;
  expenseTypeData: any;
  handleDropdownActions: (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => void;
  dropdownPage: number;
  searchLoading: string | null;
  dropdownSearchTerm: any;
  setDropdownSearchTerm: any;
  setDropdownPage: any;
  expenseTypeResponse: any;
  endActions?: ReactNode;
}

const ExpensesSearchHeader: React.FC<ExpensesSearchHeaderComponentProps> = ({
  handleChangeFilter,
  handleChangeSearch,
  handleSubmitSearch,
  searchFormData,
  filter,
  clearSearch,
  expenseTypeData,
  handleDropdownActions,
  expenseTypeResponse,
  dropdownPage,
  searchLoading,
  dropdownSearchTerm,
  setDropdownSearchTerm,
  setDropdownPage,
  endActions,
}) => {
  const { t } = useTranslation();
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const filterOptions = ["title", "type", "dateInterval"];
  const expenseTypes = expenseTypeData?.map(
    (item: { id: number; title: string }) => ({
      value: item.id,
      label: item.title,
    }),
  );
  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      handleSubmitSearch();
    }
  };

  return (
    <>
      <Grid container spacing={1} alignItems="flex-start">
        <Grid item xs={12} sm={6} md={3} lg={2}>
          <Filter
            label="Filter By"
            filterOptions={filterOptions}
            handleChangeFilter={handleChangeFilter}
            filter={filter}
          />
        </Grid>
        {filter.map((fieldName) => (
          <>
            {fieldName === "dateInterval" ? (
              <Grid item key={fieldName} xs={12} sm={6} md={3}>
                <DateRangePicker
                  label=""
                  maxDate={dayjs()}
                  placeholder={"YYYY-MM-DD → YYYY-MM-DD"}
                  onChange={(value) =>
                    handleChangeSearch("dateInterval", [
                      value?.[0] ? value[0].format("YYYY-MM-DD") : null,
                      value?.[1] ? value[1].format("YYYY-MM-DD") : null,
                    ])
                  }
                  value={[
                    searchFormData?.start ? dayjs(searchFormData.start) : null,
                    searchFormData?.end ? dayjs(searchFormData.end) : null,
                  ]}
                />
              </Grid>
            ) : fieldName === "type" ? (
              <Grid item key={fieldName} xs={12} sm={6} md={3} lg={2}>
                <DynamicSelect
                  value={
                    searchFormData?.type
                      ? {
                          value: searchFormData?.type,
                          label:
                            searchFormData?.typeTitle || searchFormData?.type,
                        }
                      : null
                  }
                  isDisabled={false}
                  isRtl={false}
                  isSearchable={true}
                  name="type"
                  options={expenseTypes}
                  isRequired={true}
                  onChange={(e) => handleChangeSearch("type", e)}
                  label=""
                  onKeyDown={handleKeyPress}
                  defaultPlaceholder={t("type")}
                  isLoading={searchLoading === "expenseTypes"}
                  onInputChange={(
                    search: string,
                    { action }: { action: string },
                  ) => {
                    if (
                      action === "input-change" ||
                      (action !== "set-value" &&
                        action !== "menu-close" &&
                        action !== "input-blur")
                    )
                      handleDropdownActions("expenseTypes", "searchIt", search);
                    else if (
                      action === "input-blur" ||
                      action === "menu-close"
                    ) {
                      if (dropdownSearchTerm || dropdownPage > 1)
                        handleDropdownActions("expenseTypes", "searchIt", "");
                      else setDropdownSearchTerm("");
                      setDropdownPage(1);
                    }
                  }}
                  onMenuScrollToBottom={() => {
                    if (!(expenseTypeResponse?.pages[0]?.data as any).last)
                      handleDropdownActions("expenseTypes", "loadNext");
                  }}
                  page={dropdownPage}
                />
              </Grid>
            ) : (
              <Grid item key={fieldName} xs={12} sm={6} md={3} lg={2}>
                <StyledField
                  fullWidth={true}
                  placeholder={t(fieldName)}
                  displayPlaceholder={true}
                  handleChange={(e) =>
                    handleChangeSearch(fieldName, e.target.value)
                  }
                  required={true}
                  // error={searchErrorMessages[fieldName]}
                  value={searchFormData?.[fieldName] || ""}
                  handleKeyDown={handleKeyPress}
                />
              </Grid>
            )}
          </>
        ))}
        <Grid
          item
          xs={12}
          sm={6}
          md={2}
          sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
        >
          <Button
            variant="contained"
            disabled={filter.length ? false : true}
            onClick={handleSubmitSearch}
            sx={{
              boxShadow: "none",
              background: filter.length > 0 ? themeColors?.primary : "#B8C0C6",
              height: 38,
              minHeight: 38,
            }}
            className={`text-white text-ellipsis overflow-hidden font-Saira font-[600] text-[13px] px-8 py-1`}
          >
            {t("submit")}
          </Button>
          {filter.length ? (
            <Button
              variant="contained"
              onClick={clearSearch}
              sx={{
                boxShadow: "none",
                color: themeColors?.primary,
                height: 38,
                minHeight: 38,
              }}
              className={`text-ellipsis overflow-hidden font-Saira font-[600] text-[13px] bg-[#d8d1e0] px-8 py-1`}
            >
              {t("clear")}
            </Button>
          ) : null}
        </Grid>
        {endActions ? (
          <Grid
            item
            xs={12}
            sm="auto"
            sx={{
              display: "flex",
              alignItems: "flex-start",
              marginInlineStart: "auto",
            }}
          >
            {endActions}
          </Grid>
        ) : null}
      </Grid>
    </>
  );
};

export default ExpensesSearchHeader;
